import type { ChatStatus, ToolUIPart as ToolPartType } from "ai";
import { getToolName, isToolUIPart } from "ai";
import type { AgentUIMessage } from "@/lib/ai/types";
import { VIZ_TOOL_NAME } from "@/lib/ai/visualization/config";
import type { VizDataset } from "@/lib/ai/visualization/data/schema";
import type { VisualizationHistoryModel } from "@/lib/ai/visualization/history";
import { buildDatasetEnvelopeForRender } from "@/lib/ai/visualization/runtime/stream-patches";
import {
  getRenderableToolOutput,
  resolveVizRenderOutput,
  resolveVizRenderToolResult,
  type ScannedVisualizationPart,
} from "@/lib/ai/visualization/shared/transport";
import type {
  VizRenderOutput,
  VizRenderToolResult,
} from "@/lib/ai/visualization/types";

/**
 * `viz_render` の toolCallId ごとに復元した dataset scope index。
 *
 * @remarks
 * tool-part renderer はこの index を参照して persisted output だけからも
 * 再描画できる必要があるため、history model と同じ shape を共有する。
 */
export type DatasetIndex = Map<string, Map<string, VizDataset>>;
type PartKeyEntry = { toolCallId: string; partKey: string };

/**
 * tool-part plugin が描画前に読む履歴派生 state。
 *
 * @remarks
 * dataset 復元と persisted viz output 解決は shared history model から受け取り、
 * plugin 固有の duplicate suppression と memo signature だけを追加で持つ。
 */
export type VizToolPartHistoryState = {
  datasetIndex: DatasetIndex;
  duplicateVizPartKeySet: Set<string>;
  vizOutputIndex: Map<string, VizRenderOutput>;
  historySignature: string;
};

/**
 * 単一 message 内で既に完了した viz toolCall 集合。
 *
 * @remarks
 * input-streaming part の重複表示抑止は message 単位で完結するため、
 * history 全体とは別に軽い state として切り出す。
 */
export type VizToolPartMessageState = {
  vizOutputToolCallIds: Set<string>;
};

/**
 * tool-part renderer が JSX 分岐前に受け取る純粋な描画モデル。
 *
 * @remarks
 * streaming / persisted output / failure summary の優先順位を React 外へ寄せ、
 * component 側の条件分岐を薄く保つ。
 */
export type VizToolPartRenderModel = {
  shouldHidePart: boolean;
  isStreaming: boolean;
  statusPanelDefaultOpen: boolean;
  shouldShowFailureOutput: boolean;
  shouldShowRawOutput: boolean;
  shouldShowVisualization: boolean;
  toolOutput: ToolPartType["output"];
  failureOutput: ToolPartType["output"] | null;
  errorText: string | undefined;
  tree: VizRenderOutput["tree"] | null;
  vizData: ReturnType<typeof buildDatasetEnvelopeForRender>;
  narrativeTexts: ReturnType<typeof extractNarrativeTexts>;
};

const vizToolPartRenderSignatureCache = new WeakMap<object, string>();

function buildDuplicateVizPartKeySet(entries: PartKeyEntry[]): Set<string> {
  const latestPartKeyByToolCallId = new Map<string, string>();
  const duplicatePartKeys = new Set<string>();

  entries.forEach(({ toolCallId, partKey }) => {
    latestPartKeyByToolCallId.set(toolCallId, partKey);
  });

  entries.forEach(({ toolCallId, partKey }) => {
    const latestPartKey = latestPartKeyByToolCallId.get(toolCallId);
    if (latestPartKey && latestPartKey !== partKey) {
      duplicatePartKeys.add(partKey);
    }
  });

  return duplicatePartKeys;
}

function buildVizRelevantPartSignature(
  part: ScannedVisualizationPart,
): string | null {
  if (part.kind === "viz-result-data") {
    return JSON.stringify({
      kind: "viz-result",
      toolCallId: part.payload.toolCallId ?? null,
      outcome: part.payload.outcome,
      reason: part.payload.reason ?? null,
      output: part.payload.output,
    });
  }

  if (part.toolName === VIZ_TOOL_NAME && part.state === "output-available") {
    const compactResult = part.vizRenderToolResult;
    if (!compactResult) {
      return null;
    }

    return JSON.stringify({
      kind: "viz-compact-result",
      toolCallId: part.toolCallId ?? null,
      datasetId: compactResult.datasetId,
      outcome: compactResult.outcome,
      followupSummary: compactResult.followupSummary ?? null,
      failure: compactResult.failure ?? null,
    });
  }

  if (part.toolName === VIZ_TOOL_NAME) {
    return null;
  }

  return JSON.stringify({
    kind: "viz-source",
    toolName: part.toolName,
    toolCallId: part.toolCallId ?? null,
    state: part.state,
    input: part.input ?? null,
    output: part.output ?? null,
    errorText: part.errorText ?? null,
  });
}

function buildVizToolPartHistorySignature(
  parts: readonly ScannedVisualizationPart[],
): string {
  return parts
    .map((part) => {
      const partSignature = buildVizRelevantPartSignature(part);
      return partSignature ? `${part.partKey}:${partSignature}` : null;
    })
    .filter((signature): signature is string => typeof signature === "string")
    .join("\n");
}

/**
 * shared history model から tool-part plugin 向けの派生 state を構築する。
 *
 * @remarks
 * `use-viz-tool-part-plugin` から pure 計算を分離し、React 側には
 * render と memoization だけを残す。compact result を signature へ含めるのは、
 * persisted tree がなくても dataset scope の更新を差分として拾うため。
 */
export function buildVizToolPartHistoryState(
  historyModel: Pick<
    VisualizationHistoryModel,
    "scannedParts" | "datasetIndex" | "vizOutputIndex"
  >,
): VizToolPartHistoryState {
  const vizPartEntries: PartKeyEntry[] = historyModel.scannedParts.flatMap(
    (part) => {
      if (
        part.kind !== "tool" ||
        part.toolName !== VIZ_TOOL_NAME ||
        part.state !== "output-available" ||
        !part.toolCallId
      ) {
        return [];
      }
      return [{ toolCallId: part.toolCallId, partKey: part.partKey }];
    },
  );

  return {
    datasetIndex: historyModel.datasetIndex,
    duplicateVizPartKeySet: buildDuplicateVizPartKeySet(vizPartEntries),
    vizOutputIndex: historyModel.vizOutputIndex,
    historySignature: buildVizToolPartHistorySignature(
      historyModel.scannedParts,
    ),
  };
}

/**
 * 1 message 内で `output-available` 済みの viz toolCallId 集合を抽出する。
 *
 * @remarks
 * input-streaming 中の同一 toolCall を隠す判定にだけ使うため、
 * `viz_render` かつ完了済みの part だけへ絞って返す。
 */
function buildVizOutputToolCallIds(message: AgentUIMessage): Set<string> {
  return new Set(
    message.parts
      .filter(isToolUIPart)
      .filter(
        (part) =>
          getToolName(part) === VIZ_TOOL_NAME &&
          part.state === "output-available" &&
          typeof part.toolCallId === "string",
      )
      .map((part) => part.toolCallId as string),
  );
}

/**
 * message 内の completed viz toolCall 集合を memo key 化する。
 *
 * @remarks
 * Set 自体は参照比較できないため、順序安定した文字列へ潰して
 * resolver cache の key として使う。
 */
function buildVizOutputToolCallIdSignature(message: AgentUIMessage): string {
  return [...buildVizOutputToolCallIds(message)].join("\n");
}

/**
 * message 単位で `output-available` 済みの viz toolCall 集合を memoize する。
 *
 * @remarks
 * input-streaming 抑止判定は message 内の completed call 集合だけ見れば足りるため、
 * React state ではなく signature cache に閉じ込めて再計算を抑える。
 */
export function createVizToolPartMessageStateResolver(): (
  message: AgentUIMessage,
) => VizToolPartMessageState {
  const cachedStates = new Map<string, VizToolPartMessageState>();

  return (message) => {
    const signature = buildVizOutputToolCallIdSignature(message);
    const cachedState = cachedStates.get(signature);
    if (cachedState) {
      return cachedState;
    }

    const nextState: VizToolPartMessageState = {
      vizOutputToolCallIds: buildVizOutputToolCallIds(message),
    };
    cachedStates.set(signature, nextState);
    return nextState;
  };
}

/**
 * plugin state が欠落しても安全に message-state 契約へ戻す。
 */
export function toVizToolPartMessageState(
  state: unknown,
): VizToolPartMessageState {
  if (!state || typeof state !== "object") {
    return {
      vizOutputToolCallIds: new Set<string>(),
    };
  }

  const candidate = state as {
    vizOutputToolCallIds?: unknown;
  };
  return {
    vizOutputToolCallIds:
      candidate.vizOutputToolCallIds instanceof Set
        ? (candidate.vizOutputToolCallIds as Set<string>)
        : new Set<string>(),
  };
}

/**
 * memo comparator 用に tool part の render 影響面だけを署名化する。
 */
export function buildVizToolPartRenderSignature(part: ToolPartType): string {
  const cachedSignature = vizToolPartRenderSignatureCache.get(part);
  if (cachedSignature) {
    return cachedSignature;
  }

  const signature = JSON.stringify({
    type: part.type,
    toolCallId: part.toolCallId ?? null,
    state: part.state,
    input: part.input ?? null,
    output: getRenderableToolOutput(part.output) ?? null,
    errorText: part.errorText ?? null,
  });
  vizToolPartRenderSignatureCache.set(part, signature);
  return signature;
}

/**
 * 2 つの viz toolCallId 集合が同一内容かを順序非依存で判定する。
 *
 * @remarks
 * message state の shallow compare では Set の参照差分だけが残るため、
 * 再描画要否を内容ベースで決める。
 */
export function areVizOutputToolCallIdsEqual(
  previous: Set<string>,
  next: Set<string>,
): boolean {
  return (
    previous.size === next.size &&
    [...previous].every((toolCallId) => next.has(toolCallId))
  );
}

function getVizModelResult(part: ToolPartType): VizRenderToolResult | null {
  const toolName = getToolName(part);
  if (toolName !== VIZ_TOOL_NAME) return null;
  return resolveVizRenderToolResult(part.output);
}

function getVizOutput(
  part: ToolPartType,
  vizOutputIndex: Map<string, VizRenderOutput>,
): VizRenderOutput | null {
  const toolName = getToolName(part);
  if (toolName !== VIZ_TOOL_NAME) return null;
  const output = getRenderableToolOutput(part.output);
  const resolved = resolveVizRenderOutput(output);
  if (resolved) return resolved;

  if (typeof part.toolCallId !== "string") return null;
  return vizOutputIndex.get(part.toolCallId) ?? null;
}

/**
 * `viz_stream` の transient tree と persisted output のどちらを描画に使うか決める。
 *
 * @remarks
 * stream 側は reset 直後に `EMPTY_VIZ_TREE` を先に配るため、完成済みの
 * `vizOutput.tree` があるカードでも一瞬だけ空 tree を掴むことがある。
 * live 中にその空 tree を優先すると「次のカードが来るまで前のカードが空白」の
 * 体験になるため、root と element がそろうまで persisted tree へフォールバックする。
 */
export function resolveVizRenderTree(args: {
  isStreaming: boolean;
  streamTree: VizRenderOutput["tree"] | undefined;
  persistedTree: VizRenderOutput["tree"] | null;
}): VizRenderOutput["tree"] | null {
  if (!args.isStreaming) {
    return args.persistedTree;
  }

  if (!isRenderableVizTree(args.streamTree)) {
    return args.persistedTree;
  }

  return args.streamTree;
}

function isRenderableVizTree(
  tree: VizRenderOutput["tree"] | undefined | null,
): tree is VizRenderOutput["tree"] {
  return Boolean(
    tree &&
      typeof tree.root === "string" &&
      tree.root.length > 0 &&
      tree.elements &&
      typeof tree.elements === "object" &&
      tree.root in tree.elements,
  );
}

/**
 * 可視化失敗時だけ、Tool パネルへ短い failure summary を出す。
 *
 * @remarks
 * 成功時の debug payload は表示しない。失敗理由は main 向け compact result と
 * 同じ summary を再利用し、UI と model で原因表現を揃える。
 */
function getFailureOutput(
  vizModelResult: VizRenderToolResult | null,
): ToolPartType["output"] | null {
  return vizModelResult?.failure ?? null;
}

function toNonBlankText(value: unknown): string | null {
  if (typeof value !== "string") return null;
  const compact = value.trim();
  return compact.length > 0 ? compact : null;
}

/**
 * Viz 出力からカード前後の表示テキストを抽出する。
 *
 * @remarks
 * fallback 時の詳細理由は tool result で確認可能なため、
 * チャット本文では露出せずノイズを抑える。
 */
export function extractNarrativeTexts(vizOutput: VizRenderOutput | null): {
  beforeCardText: string | null;
  postCardAnalysisText: string | null;
} {
  if (!vizOutput?.cardNarrative) {
    // fallback 理由は authoringError/tool result で確認できるため、
    // チャット本文側へは表示せずカード周辺のノイズを防ぐ。
    return {
      beforeCardText: null,
      postCardAnalysisText: null,
    };
  }

  return {
    beforeCardText: toNonBlankText(vizOutput.cardNarrative.beforeCardText),
    postCardAnalysisText: toNonBlankText(vizOutput.cardNarrative.afterCardText),
  };
}

/** 同じ viz tool call を表す過渡 part と重複 output part を描画対象から除外する。 */
export function shouldHideVizToolPart(args: {
  part: ToolPartType;
  isDuplicateOutputPart: boolean;
  vizOutputToolCallIds: Set<string>;
}): boolean {
  const toolCallId = args.part.toolCallId;
  return (
    (args.part.state === "output-available" && args.isDuplicateOutputPart) ||
    (args.part.state === "input-streaming" &&
      typeof toolCallId === "string" &&
      args.vizOutputToolCallIds.has(toolCallId))
  );
}

/**
 * tool part から VizRenderer / ToolOutput の表示状態を pure に導出する。
 *
 * @remarks
 * React component 側は hook と JSX だけに寄せ、履歴復元・stream 優先順位・
 * duplicate suppression はこの model へ閉じ込める。
 */
export function buildVizToolPartRenderModel(args: {
  part: ToolPartType;
  message: AgentUIMessage;
  chatStatus: ChatStatus;
  isLastMessage: boolean;
  historyDatasetIndex: DatasetIndex;
  historyVizOutputIndex: Map<string, VizRenderOutput>;
  isDuplicateOutputPart: boolean;
  vizOutputToolCallIds: Set<string>;
  streamTree: VizRenderOutput["tree"] | undefined;
}): VizToolPartRenderModel {
  const vizOutput = getVizOutput(args.part, args.historyVizOutputIndex);
  const vizModelResult = getVizModelResult(args.part);
  const toolCallId = args.part.toolCallId;
  const scopedToolCallId =
    typeof toolCallId === "string" ? toolCallId : undefined;
  const isStreaming =
    args.chatStatus === "streaming" &&
    args.message.role === "assistant" &&
    args.isLastMessage;
  const tree = resolveVizRenderTree({
    isStreaming,
    streamTree: args.streamTree,
    persistedTree: vizOutput?.tree ?? null,
  });
  const vizData = buildDatasetEnvelopeForRender(
    vizOutput ?? vizModelResult,
    args.historyDatasetIndex,
    scopedToolCallId,
  );
  const isOutputAvailable = args.part.state === "output-available";
  const hasEarlyVizOutput = vizOutput !== null;
  const failureOutput = getFailureOutput(vizModelResult);
  const shouldHidePart = shouldHideVizToolPart(args);

  return {
    shouldHidePart,
    isStreaming,
    // 成功した可視化はカード本体を主役にしたいため既定で閉じる。
    // 一方で失敗 summary や output-error は、閉じたままだと原因に辿り着きにくいので自動で開く。
    statusPanelDefaultOpen:
      args.part.state === "output-error" || failureOutput !== null,
    shouldShowFailureOutput: isOutputAvailable && failureOutput !== null,
    shouldShowRawOutput:
      args.part.state === "output-error" ||
      (isOutputAvailable && !tree && failureOutput === null),
    shouldShowVisualization:
      tree !== null &&
      vizData !== null &&
      (hasEarlyVizOutput || isOutputAvailable),
    toolOutput: getRenderableToolOutput(args.part.output),
    failureOutput,
    errorText: args.part.errorText,
    tree,
    vizData,
    narrativeTexts: extractNarrativeTexts(vizOutput),
  };
}
